import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Page from 'components/Page';
import ChangeStaffModal from './components/ChangeStaffModal';
import './ticket-detail-v2.css';
import VoidTicketModal from './components/VoidTicketModal';
import RefundTicketModal from './components/RefundTicketModal';
import { formatCurrency } from 'utils/formatCurrency';
import { API } from 'common';
import { formatPhone } from 'utils/formatPhone';
import _, { find, first } from 'lodash';
import ServicePicker from 'widgets/ServicePicker';
import shopSelectors from 'store/shop/selectors';
import { useSelector } from 'react-redux';

const TicketDetailV2 = () => {
  const servicePicker = useRef(null);
  const { ticketId } = useParams();
  const history = useHistory();
  const handleBack = () => history.replace(`/tickets`);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [changeStaffData, setIsChangeStaffModalOpen] = useState(null);
  const _staffList = useSelector(shopSelectors.staff);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [payment, setPayment] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [staffItems, setStaffItems] = useState([]);
  const initialStaffsData = useRef('');

  const staffList = useMemo(() => {
    return _staffList.filter(o => !staffItems.find(s => s.id === o.id));
  }, [_staffList, staffItems]);

  const isDisabledUpdate = useMemo(() => {
    return initialStaffsData.current === staffItems.map(o => o.id).join('<>');
  }, [staffItems]);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/report/getBillDetailV2?billId=${ticketId}`);
      const detail = res.data.payload;
      if (detail) {
        setPayment(detail);
        const _staffItems = _.chain(detail?.services || [])
          .groupBy("staffId")?.map((services, key) => {
            const o = first(services);
            return ({
              staffId: key,
              id: o.staffId,
              name: o.technician,
              avatar: '',
              services: services.map(ser => {
                return ({
                  ...ser,
                  name: ser?.serviceName, price: o.price,
                });
              })
            })
          }).value();
        initialStaffsData.current = _staffItems.map(o => o.id).join('<>');
        setStaffItems(_staffItems);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchPayment();
  }, [ticketId]);

  const ticketData = useMemo(() => {
    return {
      id: payment?.billNumber || '',
      date: payment?.strCheckInTime || '',
      customer: {
        name: payment?.customer?.name,
        phone: formatPhone(payment?.customer?.phone),
      },
      payment: {
        merchantFee: formatCurrency(payment?.cashDiscountCredit),
        discount: formatCurrency(payment?.discount),
        subTotal: formatCurrency(payment?.subTotal),
        tax: formatCurrency(payment?.tax),
        tip: formatCurrency(payment?.tip),
        total: formatCurrency(payment?.total),
        creditCard: payment?.payment?.creditCard ? {
          lastFour: payment?.payment?.creditCard?.cardNumber || '',
          type: 'VISA/ Master',
          authCode: payment?.payment?.creditCard?.authCode,
          transactionNumber: payment?.payment?.creditCard?.refnum,
          amount: payment?.payment?.creditCard?.amount,
          method: 'EMV',
        } : null,
        cash: payment?.cash?.amount || 0,
        giftCard: payment?.giftCard?.amount || 0,
        other: payment?.other?.amount || 0,
      },
      review: {
        rating: 'Good',
        comment: 'Staff professional and pay attention',
      },
      status: payment?.status === 1 ? 'CLOSED' : 'OPEN',
      refunded: 0,
      enableVoid: false,
      enableRefund: false,
    }
  }, [ticketId, payment]);

  const toggleRefundModal = () => {
    setIsRefundModalOpen(!isRefundModalOpen);
  };

  const handleVoid = () => {
    setIsVoidModalOpen(true);
  };

  const handleConfirmVoid = (ticketId) => {
    console.log('Void ticket', ticketId);
  };

  const handleAdjustTip = () => {
    history.push(`/ticketDetailV2/${ticketId}/adjust-tip`);
  };

  const handleRefund = (refundData) => {
    console.log('Processing refund:', refundData);
  };

  const handleOpenChangeStaffModal = (staffMember) => {
    setIsChangeStaffModalOpen(staffMember);
  };
  const handleAddStaff = () => {
    setIsChangeStaffModalOpen('add-new');
  }
  const handleSelectStaff = async (newStaff) => {
    setIsChangeStaffModalOpen(false);
    if (!changeStaffData) return;

    if (typeof changeStaffData === 'string') {
      if (!servicePicker.current) return;
      servicePicker.current.open([], (sers = []) => {
        const temp = [...staffItems];
        temp.push({
          staffId: newStaff.id,
          id: newStaff.id,
          name: newStaff.name,
          avatar: newStaff.avatar,
          services: sers.map(ser => {
            return ({
              tip: 0,
              discount: 0,
              quantity: 1,
              price: ser.price,
              serviceId: ser.id,
              name: ser.name,
              id: ser.id,
            });
          })
        });
        setStaffItems(temp);
      });
      return;
    };

    setStaffItems(staffItems.map(o => {
      if (o.staffId === changeStaffData?.staffId) {
        return ({
          ...o,
          staffId: newStaff?.id,
          id: newStaff?.id,
          name: newStaff?.name,
          avatar: newStaff?.avatar,
        });
      }
      return o;
    }));
  };

  const handleUpdateStaff = async () => {
    const body = [];
    staffItems.map(staff => {
      if (!staff.services) return;
      staff.services.map(service => {
        const item = {
          price: service.price,
          quantity: service.quantity,
          serviceId: service.serviceId,
          staffId: staff.staffId,
          tip: service.tip,
        };
        body.push(item);
      });
    });
    setLoading(true);
    try {
      const res = await API.post(`/bill/update-ticket-closed/${payment?.billId}`, body);
      if (res.data.payload) {
        fetchPayment();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCloseChangeStaffModal = () => {
    setIsChangeStaffModalOpen(null);
  };

  if (!ticketData) {
    return <div>Loading...</div>;
  }

  const StickFooter = () => (
    <div className='action-buttons px-3 pb-3 pt-2 bg-white'>
      {ticketData.status === 'CLOSED' && (
        <>
          {ticketData.enableVoid && (
            <button
              style={{ color: '#0C719C', background: '#ECF4FE' }}
              className='btn btn-light flex-grow-1 py-3 mr-1 d-flex align-items-center justify-content-center'
              onClick={handleVoid}
            >
              <svg
                width='25'
                height='25'
                viewBox='0 0 25 25'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M22.1408 13.2433C21.9943 11.2117 20.3587 10.325 19.889 10.1112L19.8888 5.74183C19.8888 5.01085 19.294 4.41602 18.563 4.41602H3.78962C3.05864 4.41602 2.4638 5.01085 2.4638 5.74183L2.46406 15.5276C2.46406 16.2586 3.0589 16.8534 3.78988 16.8534H11.2222C10.9627 17.1086 10.7064 17.3843 10.4622 17.6798C10.4046 17.7497 10.4046 17.8511 10.4622 17.921C12.074 19.8731 14.2214 20.9551 14.3121 21.0002C14.3387 21.0136 14.3676 21.0202 14.3964 21.0202C14.431 21.0202 14.4658 21.0106 14.4961 20.9917C14.552 20.9573 14.5858 20.8963 14.5858 20.8308V19.889C15.3544 19.9038 18.1238 19.8694 19.6625 18.8173C20.6876 18.1163 22.3727 16.4602 22.1408 13.2433ZM3.78988 16.4746C3.26773 16.4746 2.84286 16.05 2.84286 15.5276L2.84261 5.74186C2.84261 5.21953 3.26747 4.79486 3.78962 4.79486H18.563C19.0851 4.79486 19.51 5.21953 19.51 5.74186L19.5112 10.2174C19.5099 10.2303 19.5099 10.2433 19.5112 10.2562L19.5121 12.2138C19.5095 12.2323 19.2305 14.0701 16.8495 15.1977C16.0109 15.5953 14.999 15.6875 14.5858 15.7089V14.77C14.5858 14.7045 14.5519 14.6435 14.4961 14.6091C14.4404 14.5747 14.3706 14.571 14.312 14.6006C14.2421 14.6354 12.9516 15.2853 11.6258 16.4746H3.78988ZM19.4487 18.5044C17.7931 19.6368 14.4384 19.5076 14.4045 19.5051C14.3542 19.5058 14.3028 19.5221 14.2654 19.5576C14.2281 19.5935 14.207 19.6427 14.207 19.6945V20.5128C13.5937 20.1658 12.0692 19.2213 10.8556 17.8005C12.0692 16.3796 13.5936 15.4352 14.207 15.0882V15.9046C14.207 16.0093 14.2917 16.094 14.3964 16.094C14.4554 16.094 15.8563 16.0877 17.0118 15.5402C19.5759 14.3257 19.8757 12.3493 19.889 12.2397V10.5311C20.4273 10.8093 21.6438 11.6164 21.7631 13.2707C21.9814 16.2986 20.4068 17.8497 19.4487 18.5044Z'
                  fill='#0C719C'
                  stroke='#0C719C'
                />
                <path
                  d='M13.2038 13.1616L13.2038 13.1616L13.2042 13.1608C13.3496 12.8762 13.4228 12.5774 13.4228 12.2656C13.4228 11.8528 13.3367 11.5132 13.1537 11.2568C12.9828 11.0095 12.7672 10.8278 12.5073 10.7152C12.2797 10.6087 11.9903 10.5062 11.6404 10.4072V8.36125C11.812 8.41951 11.9512 8.51433 12.0609 8.64491L12.0609 8.64492L12.062 8.64623C12.1998 8.80466 12.2804 8.98062 12.3065 9.1757L12.3182 9.26244H12.4057H13.2449H13.3553L13.3444 9.15262C13.2963 8.66448 13.1146 8.26281 12.7956 7.95487C12.4957 7.65955 12.1085 7.48552 11.6404 7.42845V6.75195V6.65195H11.5404H11.0732H10.9732V6.75195V7.42657C10.4536 7.47866 10.0255 7.66565 9.6963 7.99243L9.6963 7.99243L9.69568 7.99306C9.35244 8.33981 9.18223 8.7784 9.18223 9.30097C9.18223 9.70833 9.26863 10.045 9.45179 10.3012C9.62801 10.5476 9.84279 10.7319 10.0964 10.8507C10.3245 10.9577 10.6173 11.0605 10.9732 11.1597V13.2687C10.7616 13.2134 10.5974 13.1123 10.4753 12.9682C10.3332 12.7939 10.2511 12.5886 10.2297 12.3491L10.2216 12.2579H10.1301H9.29088H9.18967L9.19089 12.3591C9.19696 12.8646 9.37421 13.2879 9.72364 13.6217L9.72436 13.6223C10.0524 13.9295 10.4707 14.1123 10.9732 14.1747V14.8331V14.9331H11.0732H11.5404H11.6404V14.8331V14.177C11.9905 14.1415 12.2968 14.038 12.5563 13.8635C12.8401 13.6764 13.0569 13.4425 13.2038 13.1616ZM10.4245 8.59717L10.4245 8.59718L10.4258 8.59594C10.5638 8.45805 10.7445 8.36681 10.9732 8.32601V10.2039C10.7246 10.1106 10.5375 10.0001 10.4069 9.87521C10.2649 9.73925 10.1869 9.53788 10.1869 9.25479C10.1869 8.96035 10.2701 8.74603 10.4245 8.59717ZM12.1922 12.9753L12.1921 12.9753L12.1911 12.9765C12.0576 13.1333 11.8762 13.2372 11.6404 13.2846V11.3612C11.884 11.4503 12.0713 11.5612 12.2066 11.6912L12.2066 11.6912L12.208 11.6925C12.3552 11.8287 12.4354 12.0297 12.4354 12.3118C12.4354 12.5719 12.3541 12.7913 12.1922 12.9753Z'
                  fill='#0C719C'
                  stroke='#0C719C'
                  stroke-width='0.2'
                />
              </svg>
              Void
            </button>
          )}

          {ticketData.enableRefund && (
            <button
              style={{ color: '#0C719C', background: '#ECF4FE' }}
              className='btn flex-grow-1 py-3 mr-1 d-flex align-items-center justify-content-center'
              onClick={toggleRefundModal}
            >
              <svg
                width='25'
                height='25'
                viewBox='0 0 25 25'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <mask id='path-1-inside-1_5545_4780' fill='white'>
                  <path d='M21.9214 8.84977C21.9219 8.84672 21.9244 8.84484 21.9247 8.8418C21.9242 8.84484 21.9219 8.84695 21.9214 8.84977Z' />
                </mask>
                <path
                  d='M21.9214 8.84977C21.9219 8.84672 21.9244 8.84484 21.9247 8.8418C21.9242 8.84484 21.9219 8.84695 21.9214 8.84977Z'
                  fill='#0C719C'
                />
                <path
                  d='M21.9214 8.84977L21.1492 8.73101L22.692 8.97816L21.9214 8.84977ZM21.9247 8.8418L22.7036 8.9017L21.1525 8.72304L21.9247 8.8418ZM22.6936 8.96852C22.6789 9.064 22.6492 9.13787 22.6271 9.18426C22.6163 9.20681 22.6066 9.22422 22.6012 9.23373C22.5968 9.24151 22.5918 9.24983 22.5945 9.24532C22.5951 9.24425 22.6016 9.23345 22.6091 9.21968C22.6171 9.20515 22.6288 9.18255 22.6412 9.15403C22.6668 9.09481 22.6954 9.00878 22.7036 8.9017L21.1457 8.7819C21.1497 8.73015 21.1586 8.6826 21.17 8.63997C21.1814 8.59775 21.1948 8.56204 21.2071 8.53377C21.2191 8.5059 21.2306 8.48393 21.2382 8.47003C21.2454 8.45689 21.2515 8.4467 21.2518 8.44624C21.2529 8.44434 21.2512 8.44718 21.2505 8.44835C21.2493 8.45043 21.2469 8.45452 21.2439 8.45969C21.2381 8.46982 21.2281 8.48787 21.217 8.51107C21.1943 8.55876 21.1641 8.63401 21.1492 8.73101L22.6936 8.96852ZM21.1525 8.72304C21.1653 8.6396 21.1894 8.57394 21.2082 8.53105C21.2174 8.50992 21.2259 8.49317 21.2314 8.48265C21.2342 8.47729 21.2365 8.47295 21.2381 8.47013C21.2395 8.46744 21.2407 8.46536 21.2408 8.46517C21.2414 8.46419 21.2402 8.46618 21.2394 8.46773C21.2383 8.46971 21.2363 8.47337 21.2338 8.47797C21.229 8.4869 21.2207 8.50275 21.2115 8.523C21.193 8.56352 21.1656 8.63204 21.1508 8.72137L22.692 8.97816C22.6774 9.06609 22.6505 9.13328 22.6326 9.17252C22.6236 9.19213 22.6157 9.20736 22.6112 9.21565C22.6089 9.21994 22.6071 9.22328 22.6061 9.22496C22.6055 9.22619 22.6045 9.22787 22.6052 9.22657C22.6055 9.22606 22.6069 9.22367 22.6085 9.22066C22.6102 9.21752 22.6128 9.21286 22.6157 9.20718C22.6216 9.19601 22.6304 9.17861 22.6399 9.15681C22.6592 9.11258 22.6838 9.04552 22.6968 8.96055L21.1525 8.72304Z'
                  fill='#0C719C'
                  mask='url(#path-1-inside-1_5545_4780)'
                />
                <path
                  d='M2.52709 4.78516V8.21383H2.52686V9.92805H2.52709V20.2138H11.9558V18.4994H4.24131V9.92805H21.3842V13.2611H23.0984V4.78516H2.52709ZM4.24131 8.21359V6.49937H21.3842V8.21359H4.24131Z'
                  fill='#0C719C'
                />
                <path
                  d='M5.09814 14.2148H10.241V15.9291H5.09814V14.2148ZM20.0984 14.2148H18.3103L19.4187 13.1065L18.2067 11.8945L15.0291 15.072L18.2065 18.2494L19.4185 17.0374L18.3101 15.9291H20.0981C20.8071 15.9291 21.3839 16.5059 21.3839 17.2148C21.3839 17.9238 20.8074 18.5006 20.0984 18.5006H13.6697V20.2148H20.0984C21.7524 20.2148 23.0984 18.8688 23.0984 17.2148C23.0984 15.5609 21.7524 14.2148 20.0984 14.2148Z'
                  fill='#0C719C'
                />
              </svg>
              Refund
            </button>
          )}
          <button
            className='btn btn-primary flex-grow-1 py-3 d-flex align-items-center justify-content-center ml-1'
            onClick={handleAdjustTip}
            style={{ color: '#0C719C', background: '#ECF4FE' }}
          >
            Adjust Tip
          </button>
          <button
            className='btn btn-primary flex-grow-1 py-3 d-flex align-items-center justify-content-center ml-1'
            onClick={handleUpdateStaff}
            disabled={isDisabledUpdate}
          >
            Update
          </button>
        </>
      )}
      {ticketData.status === 'VOIDED' && (
        <button className='tickets-voided-btn'>Voided</button>
      )}
      {ticketData.status === 'REFUNDED' && (
        <div className='ticket-refund-action-container'>
          <div className='ticket-refunded-amount'>
            <span>Refunded</span>
            <h2>{formatCurrency(ticketData.refunded)}</h2>
          </div>
          <button className={`tickets-refund-more-btn ${ticketData.refunded === ticketData.payment.total && 'disable'}`}>Refund More</button>
        </div>
      )}
    </div>
  );
  return (
    <Page onBack={handleBack} loading={isLoading} title='Ticket Details' className='pb-0' StickFooter={StickFooter}>
      <div className='ticket-detail-v2'>
        <div className='ticket-header-section py-3'>
          <div className='d-flex align-items-center mx-3'>
            <div className='ticket-id-box bg-dark p-3 mr-3'>
              <h3 className='m-0 text-white'>#{ticketData.id}</h3>
            </div>
            <div className='ticket-info'>
              <div className='text-muted'>{ticketData.date}</div>
              <h3 className='ticket-customer-name my-1'>
                {ticketData.customer.name}
              </h3>
              <div className='d-flex align-items-center'>
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='mr-2'
                >
                  <path
                    d='M14.6667 11.28V13.28C14.6673 13.4657 14.6293 13.6495 14.5553 13.8195C14.4814 13.9894 14.3732 14.1416 14.2376 14.2667C14.1021 14.3919 13.942 14.4875 13.7678 14.5475C13.5937 14.6074 13.4094 14.6303 13.2267 14.6147C11.1461 14.3932 9.15424 13.6795 7.41334 12.5267C5.78829 11.4664 4.40674 10.0848 3.34668 8.45973C2.18668 6.70673 1.47314 4.70073 1.25334 2.61306C1.23786 2.43054 1.26074 2.24656 1.32052 2.07263C1.3803 1.8987 1.47562 1.7389 1.6004 1.60345C1.72517 1.46799 1.87695 1.35976 2.04637 1.28555C2.21579 1.21134 2.3991 1.17287 2.58401 1.17306H4.58401C4.90414 1.17011 5.2157 1.28774 5.45481 1.50365C5.69393 1.71957 5.84539 2.0181 5.88001 2.33973C5.94573 2.95991 6.07117 3.56974 6.25334 4.15973C6.33665 4.4192 6.34141 4.69539 6.26701 4.95841C6.19262 5.22144 6.04225 5.45709 5.83334 5.63306L5.01334 6.45306C6.00159 8.1259 7.39411 9.51842 9.06694 10.5067L9.88694 9.68673C10.0629 9.47782 10.2986 9.32745 10.5616 9.25306C10.8246 9.17866 11.1008 9.18342 11.3603 9.26673C11.9503 9.4489 12.5601 9.57434 13.1803 9.64006C13.5053 9.6751 13.8057 9.8292 14.0216 10.0719C14.2375 10.3146 14.3524 10.6303 14.3467 10.9547L14.6667 11.28Z'
                    stroke='#374151'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <span>{ticketData.customer.phone}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Information Section */}
        <div className='ticket-info-section px-3 bg-white py-2'>
          <h4 className='section-title mb-3'>Ticket Information</h4>

          {staffItems.map((staffMember, staffIndex) => (
            <div key={staffIndex} className='staff-section mb-3'>
              <div className='staff-header d-flex justify-content-between align-items-center'>
                <div className='d-flex align-items-center'>
                  <h5 className='staff-name mb-0'>{staffMember.name}</h5>
                </div>
                {ticketData.status == 'CLOSED' && (
                  <button
                    className='btn btn-link p-0'
                    onClick={() => handleOpenChangeStaffModal(staffMember)}
                  >
                    <svg
                      width='25'
                      height='25'
                      viewBox='0 0 25 25'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M6.10979 6.2563C7.40499 4.93971 9.00433 4.12725 10.6773 3.81572L10.2599 1.5C8.1395 1.89487 6.1102 2.92646 4.47213 4.59159C0.175958 8.95871 0.175957 16.0392 4.47213 20.4063L2.49529 22.4158H8.08663V16.7321L6.10979 18.7416C2.71808 15.2939 2.71808 9.70402 6.10979 6.2563ZM22.0047 2.58419H16.4134V8.26786L18.3902 6.25838C21.7819 9.70611 21.7819 15.296 18.3902 18.7437C17.095 20.0603 15.4957 20.8727 13.8227 21.1843L14.2401 23.5C16.3605 23.1051 18.3898 22.0735 20.0279 20.4084C24.324 16.0413 24.324 8.96079 20.0279 4.59367L22.0047 2.58419Z'
                        fill='#0085E7'
                      />
                    </svg>
                  </button>
                )}
              </div>
              {staffMember.services.map((service, serviceIndex) => (
                <div
                  key={serviceIndex}
                  className='service-item d-flex justify-content-between py-1'
                >
                  <div className='service-name'>{service.name}</div>
                  <div className='service-price'>
                    {formatCurrency(service.price)}
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className='d-flex align-items-center justify-content-end'>
            <button
              className='btn btn-primary py-1 d-flex align-items-center justify-content-center ml-1'
              onClick={handleAddStaff}
              style={{ color: '#0C719C', background: '#ECF4FE' }}
            >
              Add staff
            </button>
          </div>
        </div>

        <div className='payment-detail-section px-3 mt-2 py-2 bg-white'>
          <h4 className='section-title mb-3'>Payment Detail</h4>

          <div className='payment-item d-flex justify-content-between'>
            <div className='payment-label'>Merchant Fee</div>
            <div className='payment-value'>
              {formatCurrency(ticketData.payment.merchantFee)}
            </div>
          </div>

          <div className='payment-item d-flex justify-content-between'>
            <div className='payment-label'>Discount</div>
            <div className='payment-value text-danger'>
              {formatCurrency(ticketData.payment.discount, true)}
            </div>
          </div>

          <div
            className='section-sub-item d-flex justify-content-between pt-1'
            style={{ color: '#000' }}
          >
            <h4>Sub total</h4>
            <h4>{formatCurrency(ticketData.payment.subTotal)}</h4>
          </div>

          <div className='payment-item d-flex justify-content-between'>
            <div className='payment-label'>Tax</div>
            <div className='payment-value'>
              {formatCurrency(ticketData.payment.tax)}
            </div>
          </div>

          <div className='payment-item d-flex justify-content-between'>
            <div className='payment-label'>Tip</div>
            <div className='payment-value'>
              {formatCurrency(ticketData.payment.tip)}
            </div>
          </div>

          <div
            className='section-sub-item d-flex justify-content-between pt-1'
            style={{ color: '#000' }}
          >
            <h4>Total Ticket</h4>
            <h4>{formatCurrency(ticketData.payment.total)}</h4>
          </div>
          {!!ticketData.payment.creditCard && <>
            <div
              className='payment-item d-flex justify-content-between fw-bold mt-1'
              style={{ color: '#000' }}
            >
              <h4>Credit Card {ticketData.payment.creditCard?.lastFour}</h4>
              <h4>{formatCurrency(ticketData.payment.creditCard?.amount)}</h4>
            </div>

            <div className='credit-card-details text-muted mb-2'>
              <div>Card Type: {ticketData.payment.creditCard?.type}</div>
              <div>authCode: {ticketData.payment.creditCard?.authCode}</div>
              <div>
                Trans #: {ticketData.payment.creditCard?.transactionNumber}
              </div>
              {/* <div>Method: {ticketData.payment.creditCard?.method}</div> */}
            </div>
          </>}
          {!!ticketData.payment.cash && (
            <div
              className='payment-item d-flex justify-content-between fw-bold mt-1'
              style={{ color: '#000' }}
            >
              <h4>Cash</h4>
              <h4>{formatCurrency(ticketData.payment.cash)}</h4>
            </div>
          )}
          {!!ticketData.payment.giftCard && (
            <div
              className='payment-item d-flex justify-content-between fw-bold mt-1'
              style={{ color: '#000' }}
            >
              <h4>Cash</h4>
              <h4>{formatCurrency(ticketData.payment.giftCard)}</h4>
            </div>
          )}
          {!!ticketData.payment.other && (
            <div
              className='payment-item d-flex justify-content-between fw-bold mt-1'
              style={{ color: '#000' }}
            >
              <h4>Cash</h4>
              <h4>{formatCurrency(ticketData.payment.other)}</h4>
            </div>
          )}
        </div>
        {/* <div className='px-3 bg-white py-2 mt-2'>
          <ReviewSection review={ticketData.review} />
        </div> */}
      </div>

      <ServicePicker ref={servicePicker} />

      <ChangeStaffModal
        isOpen={!!changeStaffData}
        toggle={handleCloseChangeStaffModal}
        onSelectStaff={handleSelectStaff}
        staffList={staffList}
      />

      <VoidTicketModal
        isOpen={isVoidModalOpen}
        toggle={() => setIsVoidModalOpen(!isVoidModalOpen)}
        onVoid={handleConfirmVoid}
        ticketId={ticketData.id}
      />

      <RefundTicketModal
        isOpen={isRefundModalOpen}
        toggle={toggleRefundModal}
        ticketId={ticketId}
        totalBill={ticketData.payment.total}
        onRefund={handleRefund}
      />
    </Page>
  );
};

export default TicketDetailV2;
